import { api } from '../../lib/apiClient';

export type AgentSession = {
  id: string;
  organization_id: string;
  wallet_id: string;
  wallet_address?: string;
  name: string;
  max_spend_per_tx_native?: string;
  max_spend_total_native?: string;
  spent_native: string;
  allowed_chains: string[];
  allowed_recipients: string[] | null;
  allowed_methods: string[];
  expires_at: string;
  status: 'active' | 'revoked' | 'expired';
  created_at: string;
  last_used_at?: string;
};

export type AgentSessionEvent = {
  ID: string;
  SessionID: string;
  Kind: string;
  Detail: Record<string, unknown>;
  CreatedAt: string;
};

export async function listAgentSessions(orgId: string): Promise<AgentSession[]> {
  const res = await api.get<{ data: { items: AgentSession[] } }>(`/v1/orgs/${orgId}/agent-sessions`);
  return res.data.data?.items ?? [];
}

export async function getAgentSession(orgId: string, id: string): Promise<AgentSession> {
  const res = await api.get<{ data: AgentSession }>(`/v1/orgs/${orgId}/agent-sessions/${id}`);
  return res.data.data;
}

export async function revokeAgentSession(orgId: string, id: string): Promise<void> {
  await api.post(`/v1/orgs/${orgId}/agent-sessions/${id}/revoke`);
}

export async function listAgentSessionEvents(orgId: string, id: string): Promise<AgentSessionEvent[]> {
  const res = await api.get<{ data: { items: AgentSessionEvent[] } }>(
    `/v1/orgs/${orgId}/agent-sessions/${id}/events`,
  );
  return res.data.data?.items ?? [];
}

// ----- token registry -----

export type AgentToken = {
  id: string;
  organization_id?: string;
  chain: string;
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  kind: string;
  is_default: boolean;
  created_at: string;
};

export async function listAgentTokens(orgId: string): Promise<AgentToken[]> {
  const res = await api.get<{ data: { items: AgentToken[] } }>(`/v1/orgs/${orgId}/agent-tokens`);
  return res.data.data?.items ?? [];
}

export async function createAgentToken(
  orgId: string,
  input: { chain: string; address: string; symbol: string; name: string; decimals: number; kind?: string },
): Promise<AgentToken> {
  const res = await api.post<{ data: AgentToken }>(`/v1/orgs/${orgId}/agent-tokens`, input);
  return res.data.data;
}

export async function deleteAgentToken(orgId: string, id: string): Promise<void> {
  await api.delete(`/v1/orgs/${orgId}/agent-tokens/${id}`);
}

// ----- org-wide rules -----

export type TokenRef = { chain: string; address: string };

export type TokenCap = { max_per_tx?: string; max_total?: string };

export type AgentRules = {
  blocked_chains: string[];
  blocked_recipients: string[];
  token_mode: 'allow_all' | 'deny' | 'allow_only';
  blocked_tokens: TokenRef[];
  allowed_tokens: TokenRef[];
  max_native_per_tx_cap?: string;
  max_native_total_cap?: string;
  token_caps: Record<string, TokenCap>;
  updated_at?: string;
};

export async function getAgentRules(orgId: string): Promise<AgentRules> {
  const res = await api.get<{ data: AgentRules }>(`/v1/orgs/${orgId}/agent-rules`);
  return res.data.data;
}

export async function putAgentRules(orgId: string, input: AgentRules): Promise<AgentRules> {
  const res = await api.put<{ data: AgentRules }>(`/v1/orgs/${orgId}/agent-rules`, input);
  return res.data.data;
}
